import React, { useEffect, useState } from 'react';
import { getSettings, updateSettings } from '../../api';

const TIMEZONES = ['UTC','America/New_York','America/Chicago','America/Denver','America/Los_Angeles','Europe/London','Europe/Paris','Europe/Berlin','Asia/Dubai','Asia/Kolkata','Asia/Bangkok','Asia/Singapore','Asia/Tokyo','Australia/Sydney'];
const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
const HOUR_OPTIONS = Array.from({length:13},(_,i)=>i+1);
const IDLE_OPTIONS = [2,5,10,15,20,30,60];
const INTERVAL_OPTIONS = [1,2,5,10,15,30];
const LOOKBACK_OPTIONS = [1,2,3,4,5,7,14,30];

export default function TrackSettings() {
  const [s, setS] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getSettings().then(data => {
      setS({ ...data, work_days: JSON.parse(data.work_days || '[]') });
    });
  }, []);

  const toggle = (key) => setS(prev => ({ ...prev, [key]: prev[key] === 'true' ? 'false' : 'true' }));
  const toggleDay = (day) => setS(prev => {
    const days = prev.work_days.includes(day) ? prev.work_days.filter(d => d !== day) : [...prev.work_days, day];
    return { ...prev, work_days: days };
  });

  const handleSave = async () => {
    await updateSettings({ ...s, work_days: JSON.stringify(s.work_days) });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  if (!s) return <div className="empty">Loading...</div>;

  const Section = ({ title, children }) => (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 16 }}>{title}</div>
      {children}
    </div>
  );

  const Check = ({ label, desc, settingKey }) => (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12, cursor: 'pointer' }}>
      <input type="checkbox" checked={s[settingKey] === 'true'} onChange={() => toggle(settingKey)} style={{ marginTop: 2 }} />
      <div>
        <div style={{ fontSize: '0.875rem' }}>{label}</div>
        {desc && <div className="text-muted" style={{ fontSize: '0.8rem', marginTop: 2 }}>{desc}</div>}
      </div>
    </label>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Track Settings</div>
          <div className="text-muted" style={{ fontSize: '0.875rem', marginTop: 2 }}>Configure tracking behavior for your organization</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Left column */}
        <div>
          <Section title="Work Day Settings">
            <div style={{ marginBottom: 8, fontSize: '0.875rem', color: 'var(--muted)' }}>Choose work days</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              {DAYS.map(day => (
                <label key={day} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: `1px solid ${s.work_days.includes(day) ? 'var(--primary)' : 'var(--border)'}`, borderRadius: 8, cursor: 'pointer', background: s.work_days.includes(day) ? '#eef2ff' : 'transparent', fontSize: '0.875rem' }}>
                  <input type="checkbox" checked={s.work_days.includes(day)} onChange={() => toggleDay(day)} style={{ accentColor: 'var(--primary)' }} />
                  {day.slice(0,3)}
                </label>
              ))}
            </div>
          </Section>

          <Section title="Work Time Settings">
            {[
              { label: 'Expected work hours', key: 'expected_work_hours', options: HOUR_OPTIONS, suffix: 'hour' },
              { label: 'Expected productive apps usage time', key: 'expected_productive_hours', options: HOUR_OPTIONS, suffix: 'hour' },
              { label: 'Expected active time (excl. idle)', key: 'expected_active_hours', options: HOUR_OPTIONS, suffix: 'hour' },
            ].map(f => (
              <div className="form-group" key={f.key}>
                <label>{f.label}</label>
                <select value={s[f.key]} onChange={e => setS(prev => ({ ...prev, [f.key]: e.target.value }))}>
                  {f.options.map(n => <option key={n} value={String(n)}>{n} {f.suffix}{n > 1 ? 's' : ''}</option>)}
                </select>
              </div>
            ))}
            <div className="form-group">
              <label>Organisation timezone</label>
              <select value={s.org_timezone} onChange={e => setS(prev => ({ ...prev, org_timezone: e.target.value }))}>
                {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Expected Clock-in Time</label>
              <input type="time" value={s.expected_clock_in} onChange={e => setS(prev => ({ ...prev, expected_clock_in: e.target.value }))} />
            </div>
          </Section>

          <Section title="Idle Time Settings">
            <div className="text-muted" style={{ fontSize: '0.875rem', marginBottom: 10 }}>Start to consider as idle if the employee is inactive for</div>
            <select value={s.idle_threshold_mins} onChange={e => setS(prev => ({ ...prev, idle_threshold_mins: e.target.value }))} style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8, width: '50%' }}>
              {IDLE_OPTIONS.map(n => <option key={n} value={String(n)}>{n} mins</option>)}
            </select>
          </Section>

          <Section title="Manual Time">
            <Check label="Enable manual time to everyone" settingKey="manual_time_enabled" />
            <Check label="Send manual time notifications to admins" settingKey="manual_time_notify_admin" />
          </Section>

          <Section title="Untracked (Internet-Interrupted) Hours">
            <div className="text-muted" style={{ fontSize: '0.875rem', marginBottom: 12 }}>Exclude hours with internet interruptions in total work hours?</div>
            {[
              { val: 'true', label: 'Yes – Exclude the untracked hours from total work time' },
              { val: 'false', label: 'No – Include the untracked hours to total work time' },
            ].map(opt => (
              <label key={opt.val} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10, cursor: 'pointer' }}>
                <input type="radio" checked={s.untracked_hours_exclude === opt.val} onChange={() => setS(prev => ({ ...prev, untracked_hours_exclude: opt.val }))} />
                <span style={{ fontSize: '0.875rem' }}>{opt.label}</span>
              </label>
            ))}
          </Section>
        </div>

        {/* Right column */}
        <div>
          <Section title="Screenshot Settings">
            <Check label="Enable screen capture" settingKey="screenshot_enabled" />
            <Check label="Blur screen capture" settingKey="screenshot_blur" />
            <div className="form-group" style={{ marginTop: 8 }}>
              <label>Screen capture interval</label>
              <select value={s.screenshot_interval_mins} onChange={e => setS(prev => ({ ...prev, screenshot_interval_mins: e.target.value }))}>
                {INTERVAL_OPTIONS.map(n => <option key={n} value={String(n)}>Every {n} min{n > 1 ? 's' : ''}</option>)}
              </select>
            </div>
          </Section>

          <Section title="Keyboard & Mouse Activity Tracking">
            <Check label="Enable Track Keypress Count" desc="Tracks how many keys a user presses during work hours." settingKey="track_keypress" />
            <Check label="Enable Mouse Activity Tracking" desc="Tracks mouse clicks, scrolls, and movement to measure interaction." settingKey="track_mouse" />
          </Section>

          <Section title="Email Report Settings">
            {[
              { val: 'all', label: 'Show all users in report' },
              { val: 'exclude_admin', label: 'Exclude admin users in report' },
              { val: 'exclude_heads', label: 'Exclude admin & head users in report' },
            ].map(opt => (
              <label key={opt.val} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, cursor: 'pointer' }}>
                <input type="radio" checked={s.email_report_filter === opt.val} onChange={() => setS(prev => ({ ...prev, email_report_filter: opt.val }))} />
                <span style={{ fontSize: '0.875rem' }}>{opt.label}</span>
              </label>
            ))}
          </Section>

          <Section title="Permissions">
            {[
              { key: 'perm_hide_admin_tracking', label: "Hide the admin's tracking data from the team head view" },
              { key: 'perm_employee_see_screenshots', label: 'Allow the employee to see their screen capture' },
              { key: 'perm_employee_delete_screenshots', label: 'Allow the employee to delete their screen capture' },
              { key: 'perm_heads_delete_screenshots', label: "Allow heads to delete & export their team member's screenshot" },
              { key: 'perm_employee_see_timesheet', label: 'Allow the employee to see their timesheet' },
              { key: 'perm_employee_see_timelapse', label: 'Allow the employee to see their time-lapse video' },
              { key: 'perm_heads_review_apps', label: 'Allow heads to review apps & sites' },
              { key: 'perm_employee_access_website', label: 'Allow the employee to access website' },
              { key: 'perm_hide_admin_data', label: "Hide the admin's data from all pages" },
              { key: 'perm_hide_head_data', label: "Hide the team head's data from all pages" },
              { key: 'perm_hide_track_settings_from_heads', label: 'Hide edit track settings from team heads' },
            ].map(p => <Check key={p.key} label={p.label} settingKey={p.key} />)}
          </Section>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
        <button className="btn btn-primary" onClick={handleSave} style={{ padding: '10px 28px' }}>Save Track Settings</button>
        {saved && <span className="badge badge-green">✓ Saved successfully</span>}
      </div>

      <div style={{ marginTop: 20, padding: '12px 16px', background: '#f8fafc', borderRadius: 8, fontSize: '0.8rem', color: 'var(--muted)' }}>
        ℹ️ Changes to idle time detection and screenshot capture may take up to 2 hours to reflect across all sessions.<br />
        ℹ️ Changing the timezone may affect historical data, including timesheet entries and activity logs.
      </div>
    </div>
  );
}
